import type { Dispatch } from "react";
import type { RegionCardFragment } from "../client/generated";
import type { EditorAction, EditorState } from "../editor/reducers";
import { RegionThumbnail } from "./RegionThumbnail";
import { cn } from "@acdc2/ui/lib/utils";
import { makeRegionPreviewID } from "./utils";
import { RegionContextMenuContent } from "./RegionContextMenuContent";
import { RegionTileSwitch } from "./RegionTileSwitch";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { RegionDraggable } from "./RegionDraggable";
import { RegionShortcutsHandler } from "./RegionShortcutsHandler";
import { RegionIssuesBadge } from "../issues/RegionIssuesBadge";
import {
  ContextMenu,
  ContextMenuTrigger,
} from "@acdc2/ui/components/context-menu";

type Props = {
  dispatch: Dispatch<EditorAction>;
  state: EditorState;
  fragment: RegionCardFragment;
};

export function RegionCard({ fragment, dispatch, state }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fragment.id, data: fragment });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isSelected = state.selectedRegionId === fragment.id;

  const onClick = () => {
    dispatch({
      type: "selectRegion",
      regionId: fragment.id,
    });
  };

  return (
    <div ref={setNodeRef} id={makeRegionPreviewID(fragment.id)} style={style}>
      <div
        className={cn(
          "border rounded overflow-hidden bg-gray-50 dark:bg-gray-900",
          {
            "outline-solid outline-brand": isSelected,
            "opacity-50": isDragging,
          },
        )}
      >
        <ContextMenu>
          <RegionShortcutsHandler fragment={fragment} dispatch={dispatch}>
            <ContextMenuTrigger>
              <div className="relative cursor-pointer" onClick={onClick}>
                <RegionThumbnail fragment={fragment} />
                <div className="absolute top-0 right-0 p-2">
                  <RegionIssuesBadge fragment={fragment} />
                </div>
              </div>
              <RegionTileSwitch fragment={fragment} dispatch={dispatch}>
                <RegionDraggable {...attributes} {...listeners} />
              </RegionTileSwitch>
            </ContextMenuTrigger>
          </RegionShortcutsHandler>
          <RegionContextMenuContent fragment={fragment} dispatch={dispatch} />
        </ContextMenu>
      </div>
    </div>
  );
}
